import {
  FormEvent,
  PointerEvent,
  ReactNode,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react";

import { WHATSAPP } from "../config/company";
import { Icon, IconName, LogoMark } from "./Icon";

// Building blocks for the WhatsApp conversations shown on the site. They
// copy what users really see from Hethera: plain replies, quick-reply
// buttons under a message, a message with a form button (a WhatsApp Flow),
// and the form itself, which opens over the chat. Keep the wording close to
// the backend's real messages.
//
// The phone has a fixed size that fits the viewport (--phone-h in
// global.css); the conversation moves inside it. `autoplay` plays it once
// (the hero, first visit only); inside a ScrollStage the page's scroll drives
// it. `backdrop` puts an abstract shape behind the phone (one per phone,
// each in its own colour). Without JavaScript, or with reduced motion, the chat is simply
// scrollable. The message box at the bottom is real: what the visitor types
// opens WhatsApp with that message ready to send to Hethera. On the hero,
// once the chat has played, the box types example requests and a hint says
// it's real (`invite`). The side buttons press when the mouse comes near them
// and the screen answers (volume pill, lock blink).

export function Phone({
  label,
  children,
  clock = "9:41",
  autoplay = false,
  backdrop,
}: {
  label: string;
  children: ReactNode;
  clock?: string;
  autoplay?: boolean;
  backdrop?: BackdropShape;
}) {
  const windowRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [invite, setInvite] = useState(false);
  const onPlayed = useRef(() => setInvite(true));
  useAutoplay(windowRef, trackRef, autoplay, onPlayed);
  const keys = useSideKeys();

  return (
    <figure className="phone" aria-label={label} style={{ margin: 0 }}>
      {backdrop && <Backdrop shape={backdrop} />}
      <span className="phone__key phone__key--up" aria-hidden="true" onPointerEnter={keys.up} />
      <span className="phone__key phone__key--down" aria-hidden="true" onPointerEnter={keys.down} />
      <span className="phone__key phone__key--power" aria-hidden="true" onPointerEnter={keys.power} />
      <div className={keys.locked ? "phone__screen is-locked" : "phone__screen"}>
        <span
          className={keys.volumeShown ? "phone__volume is-shown" : "phone__volume"}
          aria-hidden="true"
        >
          <span style={{ height: `${keys.volume}%` }} />
        </span>
        <div className="phone__status" aria-hidden="true">
          <span>{clock}</span>
          <span className="phone__island" />
          <StatusIcons />
        </div>
        <div className="phone__bar">
          <span className="phone__avatar">
            <LogoMark size={38} color="#0e1a13" />
          </span>
          <span className="phone__title">
            Hethera AI
            <small>Business account</small>
          </span>
        </div>
        <div className="chat-window" ref={windowRef} tabIndex={0}>
          <div className="chat" ref={trackRef}>
            {children}
            {autoplay && (
              <div className="typing" aria-hidden="true">
                <span />
                <span />
                <span />
              </div>
            )}
          </div>
        </div>
        <Composer invite={invite} />
      </div>
    </figure>
  );
}

type BackdropShape = "circle" | "pair" | "flower" | "arc";

// A scalloped disc: ten soft lobes around the centre of a 100×100 box.
const FLOWER = (() => {
  const points: string[] = [];
  for (let i = 0; i <= 120; i += 1) {
    const angle = (i / 120) * Math.PI * 2;
    const radius = 44 + 5 * Math.cos(angle * 10);
    points.push(`${(50 + radius * Math.cos(angle)).toFixed(2)},${(50 + radius * Math.sin(angle)).toFixed(2)}`);
  }
  return `M${points.join("L")}Z`;
})();

function Backdrop({ shape }: { shape: BackdropShape }) {
  return (
    <span className={`phone__backdrop phone__backdrop--${shape}`} aria-hidden="true">
      <svg viewBox="0 0 100 100" fill="currentColor">
        {shape === "circle" && <circle cx="50" cy="50" r="50" />}
        {/* Two people linked by one bill; the overlap is deeper. */}
        {shape === "pair" && (
          <>
            <circle cx="35" cy="50" r="30" fillOpacity="0.7" />
            <circle cx="65" cy="50" r="30" fillOpacity="0.7" />
          </>
        )}
        {shape === "flower" && <path d={FLOWER} />}
        {/* A thick ring in three segments (circumference 251.3): only part of
            it shows beside the phone, so a gap is always passing through. */}
        {shape === "arc" && (
          <circle
            cx="50"
            cy="50"
            r="40"
            fill="none"
            stroke="currentColor"
            strokeWidth="18"
            strokeLinecap="round"
            strokeDasharray="60 23.77"
          />
        )}
      </svg>
    </span>
  );
}

// wa.me with ?text= opens a chat with the message typed in; the short
// message link (WHATSAPP.href) can't carry text, so it's only the fallback.
function chatLink(text: string): string {
  const message = text.trim();
  if (!message || !WHATSAPP.digits) return WHATSAPP.href;
  return `https://wa.me/${WHATSAPP.digits}?text=${encodeURIComponent(message)}`;
}

// Things people really ask Hethera; the hero's box types them as examples.
const SUGGESTIONS = [
  "Buy ₦1,000 MTN airtime",
  "Pay my DStv Compact",
  "Top up my Ikeja Electric meter",
  "What can you do?",
];
const HINT_MS = 9000;

function Composer({ invite = false }: { invite?: boolean }) {
  const [text, setText] = useState("");
  const [focused, setFocused] = useState(false);
  const [touched, setTouched] = useState(false);
  const [hint, setHint] = useState(false);
  const ghost = useTypewriter(invite && !touched && !focused && !text);

  // The hint shows once, when the hero chat ends, until the box is used.
  useEffect(() => {
    if (!invite) return;
    setHint(true);
    const timer = setTimeout(() => setHint(false), HINT_MS);
    return () => clearTimeout(timer);
  }, [invite]);

  const send = (event: FormEvent) => {
    event.preventDefault();
    const opened = window.open(chatLink(text), "_blank", "noopener,noreferrer");
    if (!opened) window.location.assign(chatLink(text)); // pop-up blocked
    setText("");
  };
  const inviting = invite && !touched;
  return (
    <form className={inviting ? "phone__composer is-inviting" : "phone__composer"} onSubmit={send}>
      {hint && !touched && (
        <span className="phone__hint" aria-hidden="true">
          This box is real. Type to chat with Hethera
        </span>
      )}
      <span className="phone__field">
        <input
          className="phone__input"
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onFocus={() => {
            setFocused(true);
            setTouched(true);
            setHint(false);
          }}
          onBlur={() => setFocused(false)}
          placeholder={ghost !== null ? "" : "Message Hethera AI"}
          aria-label="Type a message to Hethera AI on WhatsApp"
          maxLength={500}
          enterKeyHint="send"
          autoComplete="off"
        />
        {ghost !== null && (
          <span className="phone__ghost" aria-hidden="true">
            {ghost}
            <i />
          </span>
        )}
      </span>
      <button className="phone__send" type="submit" aria-label="Send on WhatsApp" title="Send on WhatsApp">
        <svg width="19" height="19" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
          <path d="M3.4 20.4 21 12 3.4 3.6 3.4 10l12.6 2-12.6 2z" />
        </svg>
      </button>
    </form>
  );
}

function StatusIcons() {
  return (
    <svg
      className="phone__icons"
      width="62"
      height="12"
      viewBox="0 0 62 12"
      fill="currentColor"
    >
      <rect x="0" y="8" width="3" height="4" rx="1" />
      <rect x="5" y="5.5" width="3" height="6.5" rx="1" />
      <rect x="10" y="3" width="3" height="9" rx="1" />
      <rect x="15" y="0.5" width="3" height="11.5" rx="1" />
      <path d="M30 3.2a9 9 0 0 1 11 0l-1.4 1.6a6.8 6.8 0 0 0-8.2 0zM32.3 6a5.4 5.4 0 0 1 6.4 0l-1.5 1.7a3 3 0 0 0-3.4 0zM35.5 8.6l1.6 1.8-1.6 1.6-1.6-1.6z" />
      <rect
        x="45"
        y="1.5"
        width="14"
        height="9"
        rx="2.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        opacity="0.55"
      />
      <rect x="46.8" y="3.3" width="9.5" height="5.4" rx="1.4" />
      <rect x="60" y="4.5" width="1.5" height="3" rx="0.7" opacity="0.55" />
    </svg>
  );
}

export function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

// Types each suggestion, holds it, deletes it, then the next; null when
// inactive (the normal placeholder shows). Reduced motion: the first one,
// still.
function useTypewriter(active: boolean): string | null {
  const [shown, setShown] = useState<string | null>(null);
  useEffect(() => {
    if (!active) {
      setShown(null);
      return;
    }
    if (prefersReducedMotion()) {
      setShown(SUGGESTIONS[0]);
      return;
    }
    let item = 0;
    let length = 0;
    let deleting = false;
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      const word = SUGGESTIONS[item];
      setShown(word.slice(0, length));
      if (!deleting && length < word.length) {
        length += 1;
        timer = setTimeout(tick, 45 + Math.random() * 50);
      } else if (!deleting) {
        deleting = true;
        timer = setTimeout(tick, 1800);
      } else if (length > 0) {
        length -= 1;
        timer = setTimeout(tick, 22);
      } else {
        deleting = false;
        item = (item + 1) % SUGGESTIONS.length;
        timer = setTimeout(tick, 420);
      }
    };
    timer = setTimeout(tick, 500);
    return () => clearTimeout(timer);
  }, [active]);
  return shown;
}

// The side buttons press as the mouse comes near (CSS :hover) and the screen
// answers: volume shows the level pill, power blinks the screen as if locked.
// Mouse only, so a tap on a touch screen never blanks the phone.
const VOLUME_STEP = 12.5;
function useSideKeys() {
  const [volume, setVolume] = useState(50);
  const [volumeShown, setVolumeShown] = useState(false);
  const [locked, setLocked] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout>>();
  const lockTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(
    () => () => {
      clearTimeout(hideTimer.current);
      clearTimeout(lockTimer.current);
    },
    [],
  );

  const mouse = (event: PointerEvent) =>
    event.pointerType === "mouse" && !prefersReducedMotion();
  const nudge = (step: number) => (event: PointerEvent) => {
    if (!mouse(event)) return;
    setVolume((v) => Math.min(100, Math.max(0, v + step)));
    setVolumeShown(true);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setVolumeShown(false), 1400);
  };
  return {
    volume,
    volumeShown,
    locked,
    up: nudge(VOLUME_STEP),
    down: nudge(-VOLUME_STEP),
    power: (event: PointerEvent) => {
      if (!mouse(event)) return;
      setLocked(true);
      clearTimeout(lockTimer.current);
      lockTimer.current = setTimeout(() => setLocked(false), 260);
    },
  };
}

// Messages still below the visible part of the chat are hidden, so each one
// animates in as it arrives. `tops` are the messages' offsets in the chat.
export function markAhead(
  items: HTMLElement[],
  tops: number[],
  offset: number,
  windowHeight: number,
) {
  items.forEach((el, i) =>
    el.classList.toggle("is-ahead", tops[i] - offset > windowHeight - 28),
  );
}

const START_MS = 900;
const PAUSE_MS = 1300;
const TYPING_MS = 1100;

const isIncoming = (el: HTMLElement) =>
  el.classList.contains("bubble--in") || el.classList.contains("bubble-group");

// The hero: every time the page loads, the conversation happens live.
// It starts with the first message; each next one arrives at the bottom
// (Hethera's replies after a typing indicator) while the chat scrolls up to
// it, like WhatsApp. It plays once and stops on the last message, and from
// then on the chat is a normal scrollable one, resting on that last message.
function useAutoplay(
  windowRef: RefObject<HTMLDivElement>,
  trackRef: RefObject<HTMLDivElement>,
  enabled: boolean,
  onPlayed: RefObject<() => void>,
) {
  useEffect(() => {
    const win = windowRef.current;
    const track = trackRef.current;
    if (!enabled || !win || !track) return;

    const children = Array.from(track.children) as HTMLElement[];
    const typing = children.find((el) => el.classList.contains("typing"));
    const items = children.filter((el) => el !== typing);

    // Hand the chat back as a plain scrollable one, showing the end.
    const settle = () => {
      win.classList.remove("is-live");
      track.style.transform = "";
      typing?.classList.remove("is-typing");
      items.forEach((el) => el.classList.remove("is-pending"));
      win.scrollTop = win.scrollHeight;
    };

    if (prefersReducedMotion()) {
      settle();
      onPlayed.current?.();
      return;
    }

    let shown = 1;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let running = false;

    // Keep the newest message at the bottom of the screen.
    const follow = () => {
      const y = Math.max(0, track.offsetHeight - win.clientHeight);
      track.style.transform = `translate3d(0, ${-y}px, 0)`;
    };
    const reveal = () => {
      typing?.classList.remove("is-typing");
      const el = items[shown];
      el.classList.remove("is-pending");
      el.classList.add("is-arriving");
      shown += 1;
      follow();
      if (shown >= items.length) {
        // Let the last message finish arriving, then hand over.
        timer = setTimeout(() => {
          track.classList.add("is-instant");
          settle();
          void track.offsetHeight;
          track.classList.remove("is-instant");
          onPlayed.current?.();
        }, 900);
      } else {
        timer = setTimeout(next, PAUSE_MS);
      }
    };
    const next = () => {
      if (typing && isIncoming(items[shown])) {
        typing.classList.add("is-typing");
        follow();
        timer = setTimeout(reveal, TYPING_MS);
      } else {
        reveal();
      }
    };

    win.classList.add("is-live");
    items.forEach((el, i) => el.classList.toggle("is-pending", i > 0));
    track.classList.add("is-instant");
    follow();
    void track.offsetHeight;
    track.classList.remove("is-instant");

    const observer = new IntersectionObserver(([entry]) => {
      if (shown >= items.length) return;
      if (entry.isIntersecting && !running) {
        running = true;
        timer = setTimeout(next, START_MS);
      } else if (!entry.isIntersecting && running) {
        running = false;
        clearTimeout(timer);
      }
    });
    observer.observe(win);

    return () => {
      observer.disconnect();
      clearTimeout(timer);
      win.classList.remove("is-live");
      track.classList.remove("is-instant");
      track.style.transform = "";
      typing?.classList.remove("is-typing");
      items.forEach((el) => el.classList.remove("is-pending", "is-arriving"));
    };
  }, [windowRef, trackRef, enabled, onPlayed]);
}

export function Out({
  children,
  time,
}: {
  children: ReactNode;
  time?: string;
}) {
  return (
    <div className="bubble bubble--out">
      <span>{children}</span>
      {time && <span className="bubble__time">{time} ✓✓</span>}
    </div>
  );
}

export function In({ children, time }: { children: ReactNode; time?: string }) {
  return (
    <div className="bubble bubble--in">
      {children}
      {time && <span className="bubble__time">{time}</span>}
    </div>
  );
}

// A message with quick-reply buttons, which WhatsApp draws as separate
// buttons under the bubble.
export function InButtons({
  children,
  buttons,
  time,
}: {
  children: ReactNode;
  buttons: string[];
  time?: string;
}) {
  return (
    <div className="bubble-group">
      <In time={time}>{children}</In>
      <div className="quick-replies">
        {buttons.map((b) => (
          <span key={b} className="quick-reply">
            {b}
          </span>
        ))}
      </div>
    </div>
  );
}

// A message that opens a WhatsApp Flow (a secure form) from its button.
export function InFlow({
  children,
  cta,
  icon = "form",
  time,
}: {
  children: ReactNode;
  cta: string;
  icon?: IconName;
  time?: string;
}) {
  return (
    <div className="bubble bubble--in">
      {children}
      {time && <span className="bubble__time">{time}</span>}
      <span className="bubble__action">
        <Icon name={icon} size={15} strokeWidth={2.2} />
        {cta}
      </span>
    </div>
  );
}

// The form a Flow opens. On a real phone it covers the chat; here it's drawn
// as a sheet inside the conversation.
export function FlowSheet({
  title,
  children,
  submit,
}: {
  title: string;
  children: ReactNode;
  submit: string;
}) {
  return (
    <div className="flow-sheet" role="group" aria-label={`${title} form`}>
      <div className="flow-sheet__head">
        <span aria-hidden="true">✕</span>
        <strong>{title}</strong>
        <Icon name="lock" size={13} strokeWidth={2.2} />
      </div>
      <div className="flow-sheet__body">{children}</div>
      <span className="flow-sheet__submit">{submit}</span>
      <span className="flow-sheet__foot">Secure form inside WhatsApp</span>
    </div>
  );
}

export function FlowField({
  label,
  value,
  secret,
}: {
  label: string;
  value: string;
  secret?: boolean;
}) {
  return (
    <span className="flow-field">
      <small>{label}</small>
      <span
        className={
          secret
            ? "flow-field__value flow-field__value--secret"
            : "flow-field__value"
        }
      >
        {value}
      </span>
    </span>
  );
}

export function Done({ children }: { children: ReactNode }) {
  return (
    <span className="bubble__ok">
      <Icon name="check" size={15} strokeWidth={2.6} />
      {children}
    </span>
  );
}
